import { open, SendRateFilter } from "@fastpubsub-sdk/client";
import {
  createOverlayPingWatchdog,
  createOverlayReissueGuard,
  overlayTransportNeedsNewToken
} from "../shared/overlay-token-recovery.js";
import {
  formatLinkQuality,
  FPS_LINK_REFRESH_MS,
  FPS_PING_INTERVAL_SEC,
  startLinkQualityRefresh,
  withWsLinkQuality
} from "../shared/fps-connection.js";

/** Preset palette colors (hex). */
const PRESET_COLORS = [
  "#ff4466",
  "#ff8844",
  "#ffcc00",
  "#44cc66",
  "#44aaff",
  "#aa66ff",
  "#ffffff",
  "#888888"
];

/** Default brush size for old messages without byte 7. */
const DEFAULT_BRUSH_SIZE = 4;

/** Incoming message counter for the status bar. */
let recvCount = 0;
let sendCount = 0;

/** Current color in RGB565. */
let selectedColor = hexToRgb565("#ff4466");

/** Brush size in pixels. */
let brushSize = DEFAULT_BRUSH_SIZE;

/** SendRateFilter rate: 0 = no limit. */
let sendRateHz = 60;

/** Paint area size (width/height, updated on resize). */
let paintSize = { width: 1, height: 1 };

/** SDK session with chosen edge (client-side bootstrap). */
let fpsSession = null;

/** SDK client handle for reconnect on rate change. */
let client = null;
let messagesQueue = null;
let recvLoopGeneration = 0;

/** Demo config and AT after connect. */
let demoConfig = null;
let accessToken = null;
let selectedEdgeApiBase = "";
let selectedEdgeLabel = "";
let linkQualityLabel = "ws rtt …";
let linkQualityTimer = null;
/** Один reissue за раз + cooldown. */
const overlayReissue = createOverlayReissueGuard();
/** Живость overlay по SDK PING/PONG. */
const overlayPing = createOverlayPingWatchdog({ pingIntervalSec: FPS_PING_INTERVAL_SEC });

const statusEl = document.getElementById("status");
const canvas = document.getElementById("canvas");
const paintArea = document.getElementById("paint-area");
const paletteEl = document.getElementById("palette");
const colorPicker = document.getElementById("color-picker");
const clearSelect = document.getElementById("clear-interval");

const ctx = canvas.getContext("2d");

/** Starts demo: bootstrap, token, WebSocket, subscribe. */
async function main() {
  setupPalette();
  setupBrushButtons();
  setupRateButtons();
  setupClearTimer();
  resizeCanvas();
  window.addEventListener("resize", resizeCanvas);

  try {
    await connectClient();
    paintArea.addEventListener("pointermove", onPointerMove);
    refreshConnectedStatus();
    startStatsTimer();
  } catch (error) {
    console.error(error);
    setStatus(`error: ${error.message}`, false);
  }
}

/**
 * Full FastPubSub client flow:
 * config -> bootstrap/resolveEdge -> AT from demo-server -> WebSocket.
 */
async function connectClient() {
  demoConfig = await fetchDemoConfig();

  setStatus("bootstrap: picking nearest edge...", true);
  fpsSession = open(demoConfig.overlay);
  const edge = await fpsSession.resolveEdge({ cache: "prefer" });
  selectedEdgeLabel = `${edge.candidate.id} (${Math.round(edge.pingMs)} ms)`;
  console.log("selected edge", edge.candidate.id, edge.candidate.apiBase, Math.round(edge.pingMs));

  setStatus(`bootstrap: requesting AT for ${edge.candidate.id}...`, true);
  selectedEdgeApiBase = edge.candidate.apiBase;
  const tokenResponse = await fetchToken(edge.candidate.apiBase);
  accessToken = tokenResponse.token;

  client = await buildClient(fpsSession, accessToken);
  messagesQueue = await client.subscribe(demoConfig.tenant, demoConfig.channel);
  startReceiveLoop();
  restartLinkQualityTimer();
}

/** GET /api/v1/demo/desktop/config */
async function fetchDemoConfig() {
  const response = await fetch("/api/v1/demo/desktop/config");
  if (!response.ok) {
    throw new Error(`config API ${response.status}`);
  }
  return response.json();
}

/** POST /api/v1/demo/desktop/token — AT for selected edge api_base. */
async function fetchToken(apiBase) {
  const response = await fetch("/api/v1/demo/desktop/token", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ api_base: apiBase })
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`token API ${response.status}: ${text}`);
  }
  return response.json();
}

/**
 * Overlay не работает с текущим AT → новый AT с demo-server + новый WS.
 * @param {string} reason
 */
async function reissueOverlaySession(reason) {
  if (!overlayReissue.tryBegin() || !fpsSession || !selectedEdgeApiBase || !demoConfig) {
    return;
  }

  console.warn(`[desktop] новый AT: ${reason}`);
  setStatus("token reissue...", true);

  try {
    const tokenResponse = await fetchToken(selectedEdgeApiBase);
    accessToken = tokenResponse.token;
    recvLoopGeneration += 1;
    const oldClient = client;
    client = null;
    messagesQueue = null;
    try {
      oldClient?.close();
    } catch (closeError) {
      console.warn("[desktop] close old client", closeError);
    }
    client = await buildClient(fpsSession, accessToken);
    messagesQueue = await client.subscribe(demoConfig.tenant, demoConfig.channel);
    startReceiveLoop();
    restartLinkQualityTimer();
    refreshConnectedStatus();
  } catch (reissueError) {
    setStatus(`error: ${reissueError.message || reason}`, false);
  } finally {
    overlayReissue.end();
  }
}

/** SDK: отказ overlay или нет PONG → новый AT. */
function handleWebSocketEvent(event) {
  if (event.type === "rttMeasured") {
    overlayPing.notePong();
    return;
  }
  if (!overlayTransportNeedsNewToken(event)) {
    return;
  }
  const msg = event.error?.message || event.error?.type || "overlay";
  console.warn(`[desktop] overlay error:`, msg);
  void reissueOverlaySession(msg);
}

/** WebSocket via session.webSocket (endpoint chosen at bootstrap). */
async function buildClient(session, token) {
  let builder = session
    .webSocket(token)
    .filterTimerMode("lowLatency200Hz")
    .onWebSocketEvent(handleWebSocketEvent);
  builder = withWsLinkQuality(builder);
  if (sendRateHz > 0) {
    builder = builder.addFilter(
      demoConfig.tenant,
      demoConfig.channel,
      SendRateFilter.fromMillis(hzToIntervalMs(sendRateHz))
    );
  }
  return builder.build().then((built) => {
    overlayPing.noteConnected();
    return built;
  });
}

/** Polls WS link quality every 3 seconds. */
function restartLinkQualityTimer() {
  if (linkQualityTimer !== null) {
    clearInterval(linkQualityTimer);
    linkQualityTimer = null;
  }
  linkQualityTimer = startLinkQualityRefresh(client, (snapshot) => {
    if (overlayPing.isStale()) {
      void reissueOverlaySession(`нет PONG ${overlayPing.staleSec()}s`);
      return;
    }
    linkQualityLabel = formatLinkQuality(snapshot);
    refreshConnectedStatus();
  }, FPS_LINK_REFRESH_MS);
}

/** Status line for a healthy WebSocket session. */
function refreshConnectedStatus() {
  setStatus(
    `connected | edge ${selectedEdgeLabel} | ${linkQualityLabel} | ${rateLabel()}`,
    true
  );
}

/** Receive loop: draw points from messages. */
function startReceiveLoop() {
  const generation = ++recvLoopGeneration;

  (async () => {
    while (messagesQueue && generation === recvLoopGeneration) {
      const message = await messagesQueue.recv();
      if (!message || generation !== recvLoopGeneration) {
        break;
      }
      recvCount += 1;
      drawPointFromPayload(message.payload);
    }
  })();
}

/**
 * Pointer move: pointermove + getCoalescedEvents (for Firefox).
 * Publishes each point from the coalesced list.
 */
function onPointerMove(event) {
  if (!client || !demoConfig) {
    return;
  }

  const rect = paintArea.getBoundingClientRect();
  const events = event.getCoalescedEvents?.() ?? [event];
  for (const point of events) {
    publishPointAt(point.clientX, point.clientY, rect);
  }
}

/** Publishes one point from screen coordinates. */
function publishPointAt(clientX, clientY, areaRect) {
  const x = clientX - areaRect.left;
  const y = clientY - areaRect.top;
  if (x < 0 || y < 0 || x > areaRect.width || y > areaRect.height) {
    return;
  }

  const payload = encodePoint(x, y, areaRect.width, areaRect.height, selectedColor, brushSize);

  client.publish(demoConfig.tenant, demoConfig.channel, payload).then(() => {
    sendCount += 1;
  }).catch((error) => {
    console.error("publish error", error);
  });
}

/**
 * Encodes a point as 7 bytes:
 * x u16, y u16, color RGB565, brush size u8.
 */
function encodePoint(x, y, width, height, color, size) {
  const nx = Math.max(0, Math.min(65535, Math.round((x / width) * 65535)));
  const ny = Math.max(0, Math.min(65535, Math.round((y / height) * 65535)));
  const brush = Math.max(1, Math.min(255, Math.round(size)));
  const buf = new Uint8Array(7);
  const view = new DataView(buf.buffer);
  view.setUint16(0, nx, false);
  view.setUint16(2, ny, false);
  view.setUint16(4, color, false);
  view.setUint8(6, brush);
  return buf;
}

/** Decodes channel payload and draws a point on the canvas. */
function drawPointFromPayload(payload) {
  if (!payload || payload.length < 6) {
    return;
  }
  const view = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
  const nx = view.getUint16(0, false);
  const ny = view.getUint16(2, false);
  const color = view.getUint16(4, false);
  const size = payload.length >= 7 ? view.getUint8(6) : DEFAULT_BRUSH_SIZE;

  const x = (nx / 65535) * paintSize.width;
  const y = (ny / 65535) * paintSize.height;
  drawBrush(x, y, rgb565ToCss(color), size);
}

/** Draws a brush square on the canvas. */
function drawBrush(x, y, cssColor, size) {
  const px = Math.max(1, Math.round(size));
  ctx.fillStyle = cssColor;
  ctx.fillRect(x, y, px, px);
}

/** Rate label for the status bar. */
function rateLabel() {
  if (sendRateHz <= 0) {
    return "no limit";
  }
  return `${sendRateHz}/s`;
}

/** Converts rate to milliseconds for SendRateFilter. */
function hzToIntervalMs(hz) {
  return Math.max(1, Math.round(1000 / hz));
}

/** Converts hex #rrggbb to RGB565. */
function hexToRgb565(hex) {
  const value = hex.replace("#", "");
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return ((r >> 3) << 11) | ((g >> 2) << 5) | (b >> 3);
}

/** Converts RGB565 to a CSS color. */
function rgb565ToCss(rgb565) {
  const r = ((rgb565 >> 11) & 0x1f) * 255 / 31;
  const g = ((rgb565 >> 5) & 0x3f) * 255 / 63;
  const b = (rgb565 & 0x1f) * 255 / 31;
  return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
}

/** Fits canvas size and caches paint area coordinates. */
function resizeCanvas() {
  const rect = paintArea.getBoundingClientRect();
  canvas.width = Math.floor(rect.width);
  canvas.height = Math.floor(rect.height);
  paintSize = { width: rect.width, height: rect.height };
}

/** Builds palette color buttons. */
function setupPalette() {
  for (const hex of PRESET_COLORS) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "swatch";
    btn.style.background = hex;
    btn.title = hex;
    if (hex === "#ff4466") {
      btn.classList.add("active");
    }
    btn.addEventListener("click", () => {
      document.querySelectorAll(".swatch").forEach((el) => el.classList.remove("active"));
      btn.classList.add("active");
      selectedColor = hexToRgb565(hex);
      colorPicker.value = hex;
    });
    paletteEl.appendChild(btn);
  }

  colorPicker.addEventListener("input", () => {
    document.querySelectorAll(".swatch").forEach((el) => el.classList.remove("active"));
    selectedColor = hexToRgb565(colorPicker.value);
  });
}

/** Brush size buttons. */
function setupBrushButtons() {
  document.querySelectorAll("#brush-sizes .toggle-btn").forEach((btn) => {
    btn.addEventListener("click", () => {
      document.querySelectorAll("#brush-sizes .toggle-btn").forEach((el) => el.classList.remove("active"));
      btn.classList.add("active");
      brushSize = Number(btn.dataset.size);
    });
  });
}

/** Rate switcher: rebuilds WebSocket with a new filter. */
function setupRateButtons() {
  document.querySelectorAll("#rate-buttons .toggle-btn").forEach((btn) => {
    btn.addEventListener("click", async () => {
      document.querySelectorAll("#rate-buttons .toggle-btn").forEach((el) => el.classList.remove("active"));
      btn.classList.add("active");
      sendRateHz = Number(btn.dataset.hz);

      if (!fpsSession || !accessToken) {
        return;
      }

      setStatus("reconnecting WebSocket...", true);
      recvLoopGeneration += 1;
      const oldClient = client;
      client = null;
      messagesQueue = null;
      oldClient.close();

      try {
        client = await buildClient(fpsSession, accessToken);
        messagesQueue = await client.subscribe(demoConfig.tenant, demoConfig.channel);
        startReceiveLoop();
        restartLinkQualityTimer();
        refreshConnectedStatus();
      } catch (error) {
        setStatus(`error: ${error.message}`, false);
      }
    });
  });
}

/** Local canvas clear on a timer, or off. */
function setupClearTimer() {
  let timerId = null;

  const restart = () => {
    if (timerId !== null) {
      clearInterval(timerId);
      timerId = null;
    }
    if (clearSelect.value === "never") {
      return;
    }
    const seconds = Number(clearSelect.value);
    timerId = setInterval(() => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }, seconds * 1000);
  };

  clearSelect.addEventListener("change", restart);
  restart();
}

/** Updates recv/send counters every second; link quality refreshes separately. */
function startStatsTimer() {
  setInterval(() => {
    if (!statusEl.classList.contains("err")) {
      statusEl.textContent = `connected | edge ${selectedEdgeLabel} | ${linkQualityLabel} | ${rateLabel()} | recv ${recvCount}/s send ${sendCount}/s | brush ${brushSize}px`;
      recvCount = 0;
      sendCount = 0;
    }
  }, 1000);
}

/** Sets status text. */
function setStatus(text, ok) {
  statusEl.textContent = text;
  statusEl.classList.toggle("ok", ok);
  statusEl.classList.toggle("err", !ok);
}

main();
